/**
 * Placeholder relationship-analysis data. Replace with the graph API: the map
 * comes back as a subject plus its related parties at the requested depth.
 */

export const RISK_COLORS = {
  High: "#df3f45",
  Medium: "#ef9c17",
  Low: "#26a269",
  Unknown: "#9aa7bb",
};

export const REL_STATS = [
  {
    key: "total",
    label: "Total Relationships",
    value: "218,456",
    delta: "8.4%",
    tone: "indigo",
    icon: "network",
    color: "#7a4fd0",
    spark: [42, 46, 41, 49, 44, 52, 47, 55, 50, 58, 53, 61, 56, 64, 59, 66],
  },
  {
    key: "connected",
    label: "Connected Taxpayers",
    value: "52,731",
    delta: "6.2%",
    tone: "blue",
    icon: "users",
    color: "#1b6fd6",
    spark: [30, 34, 31, 38, 33, 41, 36, 44, 39, 47, 42, 50, 45, 52, 48, 55],
  },
  {
    key: "groups",
    label: "Potential Groups",
    value: "1,245",
    delta: "4.7%",
    tone: "amber",
    icon: "group",
    color: "#e08a00",
    spark: [22, 26, 21, 29, 24, 32, 27, 34, 29, 36, 31, 38, 33, 40, 35, 42],
  },
  {
    key: "highrisk",
    label: "High Risk Connections",
    value: "2,156",
    delta: "12.3%",
    tone: "red",
    icon: "alert",
    color: "#df3f45",
    spark: [18, 22, 19, 26, 21, 29, 24, 31, 27, 34, 29, 37, 32, 40, 35, 43],
  },
  {
    key: "intermediaries",
    label: "Intermediaries Identified",
    value: "3,842",
    delta: "5.6%",
    tone: "green",
    icon: "userCheck",
    color: "#26a269",
    spark: [26, 29, 27, 33, 30, 36, 32, 39, 35, 41, 37, 44, 40, 46, 42, 49],
  },
];

export const RELATIONSHIP_TYPES = [
  { label: "All Types", value: "" },
  { label: "Direct Ownership", value: "Direct Ownership" },
  { label: "Shared Directors", value: "Shared Directors" },
  { label: "Shared Address", value: "Shared Address" },
  { label: "Common Phone", value: "Common Phone" },
  { label: "Common Email", value: "Common Email" },
];

export const RISK_LEVELS = ["High", "Medium", "Low", "Unknown"];
export const DEPTH_LEVELS = ["1 Level", "2 Levels", "3 Levels", "4 Levels"];

/**
 * Type colours come from the validated categorical set used elsewhere in the
 * app; "Other Links" is a neutral grey rather than a sixth hue.
 */
export const TOTAL_RELATIONSHIPS = 218456;

export const TYPE_BREAKDOWN = [
  {
    label: "Direct Ownership",
    value: 84032,
    share: 38.5,
    color: "#d55e00",
    legendPrimary: "38.5%",
    legendSecondary: "(84,032)",
  },
  {
    label: "Shared Directors",
    value: 57183,
    share: 26.2,
    color: "#e69f00",
    legendPrimary: "26.2%",
    legendSecondary: "(57,183)",
  },
  {
    label: "Shared Address",
    value: 33951,
    share: 15.6,
    color: "#009e73",
    legendPrimary: "15.6%",
    legendSecondary: "(33,951)",
  },
  {
    label: "Common Phone",
    value: 21385,
    share: 9.8,
    color: "#0072b2",
    legendPrimary: "9.8%",
    legendSecondary: "(21,385)",
  },
  {
    label: "Common Email",
    value: 11805,
    share: 5.4,
    color: "#cc79a7",
    legendPrimary: "5.4%",
    legendSecondary: "(11,805)",
  },
  {
    label: "Other Links",
    value: 9100,
    share: 4.5,
    color: "#9aa7bb",
    legendPrimary: "4.5%",
    legendSecondary: "(9,100)",
  },
];

/* ------------------------------------------------------------- the map -- */

export const MAP_CENTER = {
  name: "Alpha Traders Ltd.",
  nif: "200-234-567",
  risk: "High",
  type: "company",
};

// Angles are degrees clockwise from the right, fixing each node's position.
export const MAP_NODES = [
  {
    id: "beta",
    name: "Beta Investments SA",
    nif: "300-111-222",
    risk: "High",
    type: "company",
    link: "direct",
    angle: -90,
  },
  {
    id: "joao",
    name: "João Pereira",
    nif: "987-654-321",
    risk: "Medium",
    type: "person",
    link: "indirect",
    angle: -40,
  },
  {
    id: "prime",
    name: "Prime Logistics SARL",
    nif: "400-567-890",
    risk: "Low",
    type: "company",
    link: "indirect",
    angle: 0,
  },
  {
    id: "maria",
    name: "Maria Fernandes",
    nif: "321-654-987",
    risk: "High",
    type: "person",
    link: "indirect",
    angle: 45,
  },
  {
    id: "oceanic",
    name: "Oceanic Holdings Ltd.",
    nif: "700-999-333",
    risk: "Unknown",
    type: "company",
    link: "direct",
    angle: 90,
  },
  {
    id: "luanda",
    name: "Luanda Business Center",
    nif: "600-888-222",
    risk: "Low",
    type: "company",
    link: "indirect",
    angle: 138,
  },
  {
    id: "carlos",
    name: "Carlos Mendes",
    nif: "123-456-789",
    risk: "Low",
    type: "person",
    link: "indirect",
    angle: 180,
  },
  {
    id: "global",
    name: "Global Imports SARL",
    nif: "500-678-901",
    risk: "Medium",
    type: "company",
    link: "indirect",
    angle: -140,
  },
];

/* --------------------------------------------------- risk distribution -- */

export const HIGH_RISK_CONNECTIONS = 2156;

export const CONNECTION_RISK = [
  {
    label: "High Risk",
    value: 2156,
    share: 12.3,
    color: RISK_COLORS.High,
    legendPrimary: "2,156",
    legendSecondary: "(12.3%)",
  },
  {
    label: "Medium Risk",
    value: 5842,
    share: 33.4,
    color: RISK_COLORS.Medium,
    legendPrimary: "5,842",
    legendSecondary: "(33.4%)",
  },
  {
    label: "Low Risk",
    value: 8945,
    share: 51.1,
    color: RISK_COLORS.Low,
    legendPrimary: "8,945",
    legendSecondary: "(51.1%)",
  },
  {
    label: "Unknown",
    value: 526,
    share: 3.2,
    color: RISK_COLORS.Unknown,
    legendPrimary: "526",
    legendSecondary: "(3.2%)",
  },
];

export const INTERMEDIARIES = [
  { name: "Luanda Corporate Services", connections: 342, risk: "High" },
  { name: "Angola Trust Solutions", connections: 287, risk: "High" },
  { name: "BDO Angola Consultores", connections: 241, risk: "Medium" },
  { name: "Global Business Advisors", connections: 198, risk: "Medium" },
  { name: "Prime Corporate Services", connections: 176, risk: "Low" },
];

export const POTENTIAL_GROUPS = [
  {
    id: "GRP-2026-0001",
    name: "Group Alpha-01",
    members: 12,
    activity: "Import / Export",
    risk: "High",
    confidence: 92,
    indicators: "Shared Directors, Shared Address, Common Phone",
    updated: "01 Sep 2026 10:45 AM",
  },
  {
    id: "GRP-2026-0007",
    name: "Group Beta-07",
    members: 8,
    activity: "Construction",
    risk: "Medium",
    confidence: 86,
    indicators: "Shared Address, Common Email",
    updated: "01 Sep 2026 09:15 AM",
  },
  {
    id: "GRP-2026-0003",
    name: "Group Gamma-03",
    members: 15,
    activity: "Trading",
    risk: "High",
    confidence: 94,
    indicators: "Shared Directors, Multiple Companies, Intermediary",
    updated: "01 Sep 2026 08:40 AM",
  },
  {
    id: "GRP-2026-0011",
    name: "Group Delta-11",
    members: 6,
    activity: "Services",
    risk: "Low",
    confidence: 65,
    indicators: "Common Phone, Similar Names",
    updated: "01 Sep 2026 07:55 AM",
  },
];
